export const STRUCTURED_HOUSE_RULES_FEATURE = 'pwa_structuredHouseRules';

const LEGACY_POLICY_ICONS = {
  AGERESTRICTION: 'restriction',
  CHILDFRIENDLY: 'children',
  CREDITCARDSACCEPTED: 'credit-card',
  CREDITCARDSNOTACCEPTED: 'no-credit-card',
  CURFEW: 'clock',
  NOCURFEW: 'no-curfew',
  NONSMOKING: 'no-smoke',
  NOTCHILDFRIENDLY: 'children',
  PETFRIENDLY: 'pet',
  TAXESINCLUDED: 'money',
  TAXESNOTINCLUDED: 'money',
};

const LEGACY_POLICY_KEYS = { NOTCHILDFRIENDLY: 't_HOUSERULESCHILDRENNOTALLOWED' };

const STRUCTURED_LEGACY_LABELS = {
  AGERESTRICTION: 't_HOUSERULESAGELIMITALLOWED',
  CHILDFRIENDLY: 't_HOUSERULESCHILDRENALLOWED',
  NOTCHILDFRIENDLY: 't_HOUSERULESCHILDRENNOTALLOWED',
  CURFEW: 't_HOUSERULESCURFEWALLOWED',
  NOCURFEW: 't_HOUSERULESCURFEWNOTALLOWED',
  NONSMOKING: 't_HOUSERULESNOSMOKING',
};

const POLICY_CODES = {
  tax: ['TAXESINCLUDED', 'TAXESNOTINCLUDED'],
  age: ['AGERESTRICTION'],
  children: ['CHILDFRIENDLY', 'NOTCHILDFRIENDLY'],
  creditCards: ['CREDITCARDSACCEPTED', 'CREDITCARDSNOTACCEPTED'],
  curfew: ['CURFEW', 'NOCURFEW'],
  smoking: ['NONSMOKING'],
  pets: ['PETFRIENDLY'],
};

const SMOKING_ICONS = {
  0: 'no-smoke',
  1: 'smoking-area',
  2: 'smoking-area',
};

const hasAgeValue = (value) => value !== null && value !== undefined && value !== '';
const hasMinimumAgeValue = (value) => hasAgeValue(value) && Number(value) > 0;

function createPolicyRow(id, icon, labelKey, subLabelKey = null, subLabelParams = null) {
  return {
    id,
    icon,
    labelKey,
    subLabelKey,
    subLabelParams,
  };
}

export function formatHouseRuleTime(value) {
  if (!value && value !== 0) {
    return '';
  }

  const rawValue = String(value).trim();
  const hourOnlyMatch = rawValue.match(/^(\d{1,2})$/);
  if (hourOnlyMatch) {
    return `${hourOnlyMatch[1].padStart(2, '0')}:00`;
  }

  const timeMatch = rawValue.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!timeMatch) {
    return rawValue;
  }

  return `${timeMatch[1].padStart(2, '0')}:${timeMatch[2]}`;
}

function getStructuredHouseRulesFields(houseRules = {}) {
  const source = houseRules || {};

  return {
    smoking: source.smoking ?? null,
    pets: source.pets ?? null,
    ageLimit: source.ageLimit ?? null,
    curfew: source.curfew ?? null,
    children: source.children ?? null,
    policies: Array.isArray(source.policies)
      ? source.policies.filter((code) => typeof code === 'string' && code && !code.startsWith('FEES'))
      : [],
  };
}

function getLegacyPolicyRow(policies, codes) {
  const code = codes.find((policyCode) => policies.includes(policyCode));
  if (!code) {
    return null;
  }

  return createPolicyRow(code, LEGACY_POLICY_ICONS[code], STRUCTURED_LEGACY_LABELS[code] || `t_${code}`);
}

function getAgeRestrictionRow(ageLimit) {
  if (ageLimit?.ageRestriction !== true) {
    return null;
  }

  const hasFrom = hasMinimumAgeValue(ageLimit.from);
  const hasTo = hasAgeValue(ageLimit.to);

  if (hasFrom && hasTo) {
    return createPolicyRow('ageLimit', 'restriction', 't_HOUSERULESAGELIMITALLOWED', 't_HOUSERULESAGELIMITRANGE', {
      FROM: ageLimit.from,
      TO: ageLimit.to,
    });
  }

  if (hasFrom) {
    return createPolicyRow('ageLimit', 'restriction', 't_HOUSERULESAGELIMITALLOWED', 't_HOUSERULESAGELIMITMINAGE', { FROM: ageLimit.from });
  }

  if (hasTo) {
    return createPolicyRow('ageLimit', 'restriction', 't_HOUSERULESAGELIMITALLOWED', 't_HOUSERULESAGELIMITMAXAGE', { TO: ageLimit.to });
  }

  return null;
}

function getChildrenRow(children) {
  if (children?.allowChildren === false) {
    return createPolicyRow('children', 'children', 't_HOUSERULESCHILDRENNOTALLOWED');
  }

  if (children?.allowChildren !== true) {
    return null;
  }

  const hasFrom = hasMinimumAgeValue(children.from);
  const hasTo = hasAgeValue(children.to);

  if (hasFrom && hasTo) {
    return createPolicyRow('children', 'children', 't_HOUSERULESCHILDRENALLOWED', 't_HOUSERULESCHILDRENALLOWEDRANGE', {
      FROM: children.from,
      TO: children.to,
    });
  }

  if (hasFrom) {
    return createPolicyRow('children', 'children', 't_HOUSERULESCHILDRENALLOWED', 't_HOUSERULESCHILDRENALLOWEDMINAGE', { FROM: children.from });
  }

  if (hasTo) {
    return createPolicyRow('children', 'children', 't_HOUSERULESCHILDRENALLOWED', 't_HOUSERULESCHILDRENALLOWEDMAXAGE', { TO: children.to });
  }

  return createPolicyRow('children', 'children', 't_HOUSERULESCHILDRENALLOWED');
}

function getCurfewRow(curfew) {
  if (curfew?.enabled === false) {
    return createPolicyRow('curfew', 'no-curfew', 't_HOUSERULESCURFEWNOTALLOWED');
  }

  if (curfew?.enabled !== true) {
    return null;
  }

  const start = formatHouseRuleTime(curfew.from);
  const end = formatHouseRuleTime(curfew.to);
  if (!start || !end) {
    return null;
  }

  return createPolicyRow('curfew', 'clock', 't_HOUSERULESCURFEWALLOWED', 't_CURFEWHOURS', {
    START: start,
    END: end,
  });
}

function getSmokingRow(smoking) {
  if (smoking === null || smoking === undefined || smoking === '') {
    return null;
  }

  const value = Number(smoking);
  if (Number.isNaN(value) || !SMOKING_ICONS[value]) {
    return null;
  }

  if (value === 0) {
    return createPolicyRow('smoking', SMOKING_ICONS[value], 't_HOUSERULESNOSMOKING');
  }

  const subLabelKey = value === 1 ? 't_HOUSERULESSMOKINGINDESIGNATEDAREAS' : 't_HOUSERULESSMOKINGANYWHERE';
  return createPolicyRow('smoking', SMOKING_ICONS[value], 't_HOUSERULESSMOKING', subLabelKey);
}

function getPetsRow(pets) {
  if (pets?.allowed === false) {
    return createPolicyRow('pets', 'no-pet', 't_HOUSERULESPETSNOTALLOWED');
  }

  if (pets?.allowed === true) {
    return createPolicyRow('pets', 'pet', 't_HOUSERULESPETSALLOWED');
  }

  return null;
}

function getStructuredOrLegacyRow(structuredValue, structuredRow, legacyRow) {
  return structuredValue == null ? structuredRow || legacyRow : structuredRow;
}

export function buildHouseRulesPolicyRows(houseRules, isStructuredEnabled) {
  const {
    policies,
    smoking,
    pets,
    ageLimit,
    curfew,
    children,
  } = getStructuredHouseRulesFields(houseRules);

  if (!isStructuredEnabled) {
    return policies
      .map((code) => createPolicyRow(code, LEGACY_POLICY_ICONS[code], LEGACY_POLICY_KEYS[code] || `t_${code}`))
      .filter((row) => row.icon);
  }

  return [
    getLegacyPolicyRow(policies, POLICY_CODES.tax),
    getStructuredOrLegacyRow(ageLimit, getAgeRestrictionRow(ageLimit), getLegacyPolicyRow(policies, POLICY_CODES.age)),
    getStructuredOrLegacyRow(children, getChildrenRow(children), getLegacyPolicyRow(policies, POLICY_CODES.children)),
    getLegacyPolicyRow(policies, POLICY_CODES.creditCards),
    getStructuredOrLegacyRow(curfew, getCurfewRow(curfew), getLegacyPolicyRow(policies, POLICY_CODES.curfew)),
    getStructuredOrLegacyRow(smoking, getSmokingRow(smoking), getLegacyPolicyRow(policies, POLICY_CODES.smoking)),
    getStructuredOrLegacyRow(pets, getPetsRow(pets), getLegacyPolicyRow(policies, POLICY_CODES.pets)),
  ].filter(Boolean);
}

export function resolveHouseRulesPolicyRows(houseRules, isStructuredEnabled, translate) {
  return buildHouseRulesPolicyRows(houseRules, isStructuredEnabled).map((row) => ({
    id: row.id,
    icon: row.icon,
    label: translate(row.labelKey),
    subLabel: row.subLabelKey ? translate(row.subLabelKey, row.subLabelParams) : null,
  }));
}

export function getHouseRulesTrackingFields(houseRules, isStructuredEnabled) {
  const policyNames = {
    TAXESINCLUDED: 'tax',
    TAXESNOTINCLUDED: 'tax',
    AGERESTRICTION: 'age',
    ageLimit: 'age',
    CHILDFRIENDLY: 'children',
    NOTCHILDFRIENDLY: 'children',
    children: 'children',
    CREDITCARDSACCEPTED: 'credit_cards',
    CREDITCARDSNOTACCEPTED: 'credit_cards',
    CURFEW: 'curfew',
    NOCURFEW: 'curfew',
    curfew: 'curfew',
    NONSMOKING: 'smoking',
    smoking: 'smoking',
    PETFRIENDLY: 'pets',
    pets: 'pets',
  };
  const structuredFields = getStructuredHouseRulesFields(houseRules);

  return {
    house_rules_displayed: buildHouseRulesPolicyRows(houseRules, isStructuredEnabled)
      .map((row) => policyNames[row.id])
      .filter(Boolean)
      .join('|'),
    new_house_rules_service_flag: Boolean(isStructuredEnabled) && [
      structuredFields.smoking,
      structuredFields.pets,
      structuredFields.ageLimit,
      structuredFields.curfew,
      structuredFields.children,
    ].some((field) => field !== null),
  };
}
